import React, { useEffect, useState } from "react";
import DetailsComponent from "./details.page";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { AccessRights } from "lib/constants/accessRights";
import { ROOT } from "../../config/serial-number.route";
import {
  getDetailsSerialNumber,
  getSerialNumberActivityLog,
  resetSNScanCount,
  updateSNStatus,
} from "../../redux";
import { addAlert, selectLoading } from "modules/notification";
import LoadingComponent from "components/loading";
import GeneralDialog from "components/dialog/generalDialog";
import Alert from "assets/svg/alert.svg";
import { resetSerialNumberDetail } from "modules/serial-number/redux";
import { getLang } from "app/feature/constants";
import UpdateSnAdtInfoDialog from "modules/serial-number/components/dialog/updateSnAdtInfoDialog";
import {canAccess} from "../../../profile/redux/selector/profile.selector";
import { unwrapResult } from '@reduxjs/toolkit';
import ConfirmationDialog from 'components/dialog/confirmation';

function DetailsContainer() {
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.constant.languages);
  const match = useRouteMatch();
  const enc = match.params.enc;
  const hasSerialNumberEditAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.Qr.SERIAL_NUMBER_UPDATE));
  const hasSerialNumberViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.Qr.SERIAL_NUMBER_READ));

  const canUpdate = useSelector((state) =>
    canAccess(state, AccessRights.Qr.SERIAL_NUMBER_UPDATE));
  const canUpdateStatus = useSelector((state) =>
    canAccess(state, AccessRights.Qr.SERIAL_NUMBER_SUSPEND_REACTIVATE));
  const canReset = useSelector((state) =>
    canAccess(state, AccessRights.Qr.SERIAL_NUMBER_UPDATE_RESET_SCAN_COUNT));

  const serialNumber = useSelector((state) => state.serialNumber.serialNumber);
  const isLoading = useSelector((state) =>
    selectLoading(state, getDetailsSerialNumber.typePrefix)
  );
  const resetCountIsLoading = useSelector((state) =>
    selectLoading(state, resetSNScanCount.typePrefix)
  );
  const userLang = useSelector((state) => state.profile.language);
  const isUpdatingSNStatus = useSelector((state) =>
    selectLoading(state, updateSNStatus.typePrefix)
  );

  const [resetConfirmationDialogModal, setResetConfirmationDialogModal] =
    useState(false);
  const [showMoreDetail, setShowMoreDetail] = useState(false);
  const [showEditAdtDialog, setShowEditAdtDialog] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(resetSerialNumberDetail());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!hasSerialNumberViewAccessRight) {
      history.push("/admin/dashboard");
    }
  }, [history, hasSerialNumberViewAccessRight]);

  useEffect(() => {
    if (enc) {
      dispatch(getDetailsSerialNumber({ enc }));
      dispatch(getSerialNumberActivityLog(enc));
    } else {
      history.push(ROOT);
    }
  }, [history, enc, dispatch]);

  if (!hasSerialNumberViewAccessRight) return <LoadingComponent />;

  let tabLabels = [];
  if (hasSerialNumberViewAccessRight) {
    tabLabels.push(getLang(lang, "label.DETAILS"));
  }

  const handleCopy = (urlPrefix) => {
    navigator.clipboard.writeText(`${urlPrefix}${enc}`);
    dispatch(
      addAlert({
        severity: "success",
        message: getLang(lang, "message.success.LINK_COPIED_TO_CLIPBOARD"),
      })
    );
  };

  const resetCount = () => {
    dispatch(resetSNScanCount({ enc: enc })).then((response) => {
      setResetConfirmationDialogModal(false);
      if (response.payload.code === "success.OK") {
        // add alert if success
        dispatch(
          addAlert({
            severity: "success",
            message: getLang(lang, "message.success.SUCCESS_RESET_SCAN_COUNT"),
          })
        );
      }
    });
  };

  const handleResetCount = () => {
    setResetConfirmationDialogModal(true);
  };

  const handleUpdateSnStatus = () => {
    if (serialNumber.card.status === "active") {
      setIsDeactivateDialogOpen(true);
    } else {
      setIsActivateDialogOpen(true);
    }
  };

  const handleStatusSubmit = () => {
    dispatch(updateSNStatus({
      enc,
      status: serialNumber.card.status === "active" ? "suspend" : "active"
    }))
      .then(unwrapResult)
      .then(() => {
        setIsDeactivateDialogOpen(false);
        setIsActivateDialogOpen(false);
      });
  };

  return (
    <>
      <DetailsComponent
        serialNumber={serialNumber}
        isLoading={isLoading}
        handleCopy={handleCopy}
        handleResetCount={handleResetCount}
        hasSerialNumberEditAccessRight={hasSerialNumberEditAccessRight}
        showMoreDetail={showMoreDetail}
        setShowMoreDetail={setShowMoreDetail}
        lang={lang}
        userLang={userLang}
        handleEditAdtInfo={() => setShowEditAdtDialog(true)}
        handleUpdateSnStatus={handleUpdateSnStatus}
        canUpdate={canUpdate}
        canUpdateStatus={canUpdateStatus}
        canReset={canReset}
      />
      <UpdateSnAdtInfoDialog
        enc={enc}
        isOpen={showEditAdtDialog}
        handleClose={() => setShowEditAdtDialog(false)}
        additionalInfos={serialNumber.info.additionalInfos}
      />
      <GeneralDialog
        isLoading={resetCountIsLoading}
        isOpen={resetConfirmationDialogModal}
        handleClose={() => setResetConfirmationDialogModal(false)}
        handleProceed={resetCount}
        title={getLang(lang, "label.RESET_COUNT")}
        description={getLang(lang, "paragraph.RESET_COUNT_QUESTION")}
        icon={<img src={Alert} alt="" />}
        type={"danger"}
        lang={lang}
      />
      <ConfirmationDialog
        isOpen={isActivateDialogOpen}
        handleClose={() => setIsActivateDialogOpen(false)}
        handleProceed={handleStatusSubmit}
        isLoading={isUpdatingSNStatus}
        type={"success"}
        description={getLang(lang, "paragraph.ACTIVATE_SN_QUESTION")}
        title={getLang(lang, "label.REACTIVATE_SERIAL_NUMBER")}
      />
      <ConfirmationDialog
        isOpen={isDeactivateDialogOpen}
        handleClose={() => setIsDeactivateDialogOpen(false)}
        handleProceed={handleStatusSubmit}
        isLoading={isUpdatingSNStatus}
        type={"danger"}
        description={getLang(lang, "paragraph.SUSPEND_SN_QUESTION")}
        title={getLang(lang, "label.SUSPEND_SERIAL_NUMBER")}
      />
    </>
  );
}

export default DetailsContainer;
